import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useState, useRef } from 'react';
import { trpc } from '@/trpc';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, CheckCircle2, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { waitForClerk } from '@/utils/clerk';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/applications/$lookupId/recommendations')(
  {
    beforeLoad: async (_ctx) => {
      // Wait for Clerk to load
      const user = await waitForClerk();
      if (!user) {
        return redirect({
          to: '/',
          replace: true,
        });
      }

      // Note: We can't directly query the database here in beforeLoad
      // The ownership check will be done in the component via the query
      // But we redirect here for unauthenticated users
    },
    component: ApplicationRecommendationsPage,
  },
);

function ApplicationRecommendationsPage() {
  const { lookupId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [applicationTitle, setApplicationTitle] = useState<string>('');
  const [autoGenerationAttempted, setAutoGenerationAttempted] = useState(false);
  const prevNeedsRegenerationRef = useRef<boolean | undefined>(undefined);
  const recsTrackedRef = useRef(false);

  // Fetch application by lookupId to get applicationId
  const applicationQuery = trpc.applications.getByLookupId.useQuery(
    { lookupId },
    { enabled: !!user?.id },
  );

  // Update applicationId when application is loaded and verify ownership
  useEffect(() => {
    if (applicationQuery.data) {
      // Verify that the current user owns this application
      if (applicationQuery.data.userId !== user?.id) {
        // User doesn't own this application, redirect them
        navigate({ to: '/', replace: true });
        return;
      }

      setApplicationId(applicationQuery.data.id);
      setApplicationTitle(
        `${applicationQuery.data.position} at ${applicationQuery.data.company}`,
      );
    }
  }, [applicationQuery.data, user?.id, navigate]);

  // Fetch existing recommendations or generate new ones
  const recommendationsQuery = trpc.applicationRecommendations.get.useQuery(
    { applicationId: applicationId! },
    { enabled: !!user?.id && !!applicationId },
  );

  const utils = trpc.useUtils();

  const generateMutation = trpc.applicationRecommendations.generate.useMutation(
    {
      onSuccess: (_data: unknown) => {
        toast.success('Application-specific recommendations generated!');
        recommendationsQuery.refetch();
        utils.applicationRecommendations.getRecommendationQuotaStatus.invalidate();
        setAutoGenerationAttempted(true);
      },
      onError: (error: { message?: string }) => {
        toast.error(error.message || 'Failed to generate recommendations');
        setAutoGenerationAttempted(true);
      },
    },
  );

  const handleReturnToApplication = () => {
    navigate({ to: `/applications/${lookupId}` });
  };

  // Fire the analytics event whenever recommendations are actually shown —
  // freshly generated OR loaded from cache. This is a user-initiated path, so
  // it counts toward the Resume Value funnel just like the general resume route.
  useEffect(() => {
    if (!recsTrackedRef.current && recommendationsQuery.data?.recommendations) {
      analytics.track('resume_recommendations_generated');
      recsTrackedRef.current = true;
    }
  }, [recommendationsQuery.data?.recommendations]);

  // Auto-start generation if needed (only once per needsRegeneration state)
  useEffect(() => {
    // Only proceed if query has finished loading and we have an applicationId
    if (
      !applicationId ||
      recommendationsQuery.isLoading ||
      recommendationsQuery.isFetching
    ) {
      return;
    }

    const recommendations = recommendationsQuery.data?.recommendations;
    const needsRegeneration = recommendationsQuery.data?.needsRegeneration;
    const isGenerating = generateMutation.isLoading;

    console.error('🔍 Checking if generation needed (application-specific):', {
      hasRecommendations: !!recommendations,
      needsRegeneration,
      isGenerating,
      autoGenerationAttempted,
      isLoading: recommendationsQuery.isLoading,
      isFetching: recommendationsQuery.isFetching,
      applicationId,
    });

    // Only auto-generate once per needsRegeneration state change
    // If it fails, user must manually retry by going back and returning
    if (
      (!recommendations || needsRegeneration) &&
      !isGenerating &&
      !autoGenerationAttempted
    ) {
      console.error('🚀 Auto-starting generation (application-specific)');
      generateMutation.mutate({ applicationId });
    }
  }, [
    applicationId,
    recommendationsQuery.isLoading,
    recommendationsQuery.isFetching,
    recommendationsQuery.data?.recommendations,
    recommendationsQuery.data?.needsRegeneration,
    generateMutation.isLoading,
    autoGenerationAttempted,
  ]);

  // Reset auto-generation flag only when needsRegeneration transitions from false to true
  // This prevents infinite loops when generation fails (needsRegeneration stays true)
  useEffect(() => {
    const currentNeedsRegeneration =
      recommendationsQuery.data?.needsRegeneration;
    const prevNeedsRegeneration = prevNeedsRegenerationRef.current;

    // Only reset if we transition from false → true (genuine new regeneration need)
    if (prevNeedsRegeneration === false && currentNeedsRegeneration === true) {
      setAutoGenerationAttempted(false);
    }

    // Update the ref for next comparison
    prevNeedsRegenerationRef.current = currentNeedsRegeneration;
  }, [recommendationsQuery.data?.needsRegeneration]);

  // Loading state
  if (recommendationsQuery.isLoading || applicationQuery.isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 p-6'>
        <div className='max-w-7xl mx-auto'>
          <Button
            variant='outline'
            onClick={handleReturnToApplication}
            className='mb-6 flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Application
          </Button>
          <div className='flex items-center justify-center h-96'>
            <p className='text-muted-foreground'>
              Loading application-specific recommendations...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (recommendationsQuery.error || applicationQuery.error) {
    return (
      <div className='min-h-screen bg-gray-50 p-6'>
        <div className='max-w-7xl mx-auto'>
          <Button
            variant='outline'
            onClick={handleReturnToApplication}
            className='mb-6 flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Application
          </Button>
          <Card className='p-6 border-destructive'>
            <p className='text-destructive font-semibold'>
              Error loading recommendations
            </p>
            <p className='text-sm text-muted-foreground mt-2'>
              Please try again or contact support if the problem persists.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const recommendations = recommendationsQuery.data?.recommendations;
  const needsRegeneration = recommendationsQuery.data?.needsRegeneration;

  // If recommendations need to be generated (show processing indicator)
  if (!recommendations || needsRegeneration) {
    return (
      <div className='min-h-screen bg-gray-50 p-6'>
        <div className='max-w-7xl mx-auto'>
          <Button
            variant='outline'
            onClick={handleReturnToApplication}
            className='mb-6 flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Application
          </Button>
          <Card className='p-8'>
            <div className='flex flex-col items-center justify-center min-h-96 gap-6'>
              <div className='flex items-center justify-center'>
                <Loader className='h-8 w-8 animate-spin text-blue-600' />
              </div>
              <div className='text-center'>
                <h1 className='text-2xl font-bold mb-2'>
                  Generating Tailored Recommendations
                </h1>
                <p className='text-muted-foreground'>
                  {needsRegeneration
                    ? "Your resume or application has been updated. We're analyzing it now..."
                    : "We're analyzing your resume for the " +
                      applicationTitle +
                      ' position and creating tailored recommendations...'}
                </p>
              </div>
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <div className='flex gap-1'>
                  <div className='h-2 w-2 bg-blue-600 rounded-full animate-bounce'></div>
                  <div
                    className='h-2 w-2 bg-blue-600 rounded-full animate-bounce'
                    style={{ animationDelay: '0.1s' }}
                  ></div>
                  <div
                    className='h-2 w-2 bg-blue-600 rounded-full animate-bounce'
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Display recommendations
  const recommendationsArray = Array.isArray(recommendations)
    ? recommendations
    : [];
  const withRecommendations = recommendationsArray.filter(
    (r: any) => r.hasRecommendation,
  );
  const withoutRecommendations = recommendationsArray.filter(
    (r: any) => !r.hasRecommendation,
  );

  return (
    <div className='min-h-screen bg-gray-50 p-6'>
      <div className='max-w-7xl mx-auto'>
        <Button
          variant='outline'
          onClick={handleReturnToApplication}
          className='mb-6 flex items-center gap-2'
        >
          <ArrowLeft className='h-4 w-4' />
          Back to Application
        </Button>

        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>
            Tailored Resume Recommendations
          </h1>
          <p className='text-gray-600 mt-2'>
            Personalized suggestions to strengthen your resume for the{' '}
            <span className='font-semibold'>{applicationTitle}</span> position.
          </p>
        </div>

        <div className='space-y-6'>
          {/* Sections with recommendations */}
          {withRecommendations.length > 0 && (
            <div>
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>
                Sections with Recommendations ({withRecommendations.length})
              </h2>
              <div className='space-y-4'>
                {withRecommendations.map((rec: any, idx: number) => (
                  <Card key={idx} className='p-6'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      {rec.sectionName}
                    </h3>

                    <div className='mb-4 p-3 bg-amber-50 border border-amber-200 rounded'>
                      <p className='text-sm text-amber-900 font-medium mb-1'>
                        Why this helps for {applicationTitle.split(' at ')[0]}:
                      </p>
                      <p className='text-sm text-amber-800'>
                        {rec.justification}
                      </p>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      {/* Original */}
                      <div>
                        <h4 className='text-sm font-semibold text-gray-700 mb-2'>
                          Current
                        </h4>
                        <div className='bg-gray-100 p-3 rounded text-sm text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono text-xs'>
                          {rec.sectionText}
                        </div>
                      </div>

                      {/* Recommended */}
                      <div>
                        <h4 className='text-sm font-semibold text-green-700 mb-2'>
                          Recommended
                        </h4>
                        <div className='bg-green-50 p-3 rounded text-sm text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono text-xs border-l-4 border-green-500'>
                          {rec.recommendedText}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            rec.recommendedText,
                          );
                          toast.success('Copied to clipboard!');
                        } catch (error) {
                          toast.error('Failed to copy to clipboard');
                        }
                      }}
                      className='mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium'
                    >
                      Copy Recommended Text
                    </button>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Sections without recommendations */}
          {withoutRecommendations.length > 0 && (
            <div>
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>
                Sections Looking Strong ({withoutRecommendations.length})
              </h2>
              <div className='space-y-3'>
                {withoutRecommendations.map((rec: any, idx: number) => (
                  <Card key={idx} className='p-4 bg-green-50 border-green-200'>
                    <div className='flex items-center gap-3'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0' />
                      <div>
                        <h3 className='font-semibold text-green-900'>
                          {rec.sectionName}
                        </h3>
                        <p className='text-sm text-green-700'>
                          No changes needed - this section is strong for this
                          position!
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
